// src/pages/portal/Page_Styles/StylePreviews.js
// Espejos de los componentes reales para la vista previa del editor. Markup fijo
// (no acoplado a API/datos) pero que CONSUME los mismos tokens --edu-*, asi el
// diseñador ve el objeto reaccionando en vivo. Un espejo por bloque de la
// anatomia (ver pageAnatomy.js).
//   - `slot(token)` devuelve el valor actual de una imagen/texto editable.
//   - `hot` es el id del control con hover; cada espejo ilumina el elemento
//     concreto que ese control modifica (resaltado por elemento).
import React from "react";
import {
  BulbOutlined,
  RightOutlined,
  AppstoreOutlined,
  EyeOutlined,
  UnorderedListOutlined,
  LoginOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  MessageOutlined,
  RobotOutlined,
  PauseCircleOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { CATEGORY_TILES } from "../../../ui/theme/styleDefaults";
import { TEACHER_SCOPES } from "../../../ui/components/AppSidebar/navConfig";
import styles from "./Page_Styles.module.scss";

const cx = (...c) => c.filter(Boolean).join(" ");
// Helper de resaltado: clase si el control con hover es uno de estos tokens.
const mkOn =
  (hot) =>
  (...tokens) =>
    tokens.includes(hot) ? styles.partHot : "";

// ---- Espejo: menu lateral (layout izquierdo) ----
function SidebarMirror({ slot, hot }) {
  const on = mkOn(hot);
  const logo = slot?.("image.logo.full");
  const items = TEACHER_SCOPES.map((s) => ({
    key: s.labelKey,
    name: slot?.(`text.${s.labelKey}`) || "—",
  }));
  return (
    <div className={cx(styles.mSidebar, on("color.sidebarBg"))}>
      <div className={cx(styles.mSidebarLogo, on("image.logo.full"))}>
        {logo ? <img src={logo} alt="" /> : <span>EDUCA360</span>}
      </div>
      {items.map((it, i) => (
        <div
          key={`${it.key}-${i}`}
          className={cx(
            styles.mSideItem,
            i === 0 && styles.mSideItemActive,
            on(
              `text.${it.key}`,
              "sidebar.itemText",
              "sidebar.itemRadius",
              "sidebar.itemHoverBg",
            ),
            i === 0 && on("sidebar.itemActiveBg", "sidebar.itemActiveText"),
          )}
        >
          {it.name}
        </div>
      ))}
    </div>
  );
}

// ---- Espejo: cabecera superior (layout superior) ----
function TopbarMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div className={cx(styles.mTopbar, on("color.surface", "color.border"))}>
      <span className={styles.mTopbarTitle}>Educa360</span>
      <span className={cx(styles.mTopbarBtn, on("color.primary"))}>
        <PoweroffOutlined />
      </span>
    </div>
  );
}

// ---- Espejo: guia rapida (cabecera CTA + pasos) ----
function GuideMirror({ slot, block, hot }) {
  const on = mkOn(hot);
  const prefix = block?.guidePrefix || "salas.guide";
  const count = block?.guideSteps || 3;
  const title = slot?.(`text.${prefix}.title`) || "Guia rapida";
  const steps = Array.from({ length: count }, (_, i) => i + 1).map((n) => ({
    n,
    img: slot?.(`image.${prefix}.step${n}`),
    title: slot?.(`text.${prefix}.step${n}.title`) || `Paso ${n}`,
    desc:
      slot?.(`text.${prefix}.step${n}.desc`) || "Descripcion breve del paso.",
  }));
  return (
    <div className={styles.mGuide}>
      <div
        className={cx(
          styles.mGuideCta,
          on("color.tintSoft", "radius.card", `text.${prefix}.title`),
        )}
      >
        <span className={styles.mGuideCtaLeft}>
          <BulbOutlined
            className={cx(styles.mGuideBulb, on("color.primary"))}
          />{" "}
          {title}
        </span>
        <span className={styles.mGuideCtaRight}>
          Ver guia <RightOutlined />
        </span>
      </div>
      <div className={styles.mGuideSteps}>
        {steps.map((s) => (
          <div className={styles.mStep} key={s.n}>
            <div
              className={cx(styles.mStepIllu, on(`image.${prefix}.step${s.n}`))}
            >
              <span className={styles.mStepBadge}>{s.n}</span>
              {s.img ? <img src={s.img} alt="" /> : null}
            </div>
            <div className={styles.mStepText}>
              <div
                className={cx(
                  styles.mStepTitle,
                  on(`text.${prefix}.step${s.n}.title`, "color.text"),
                )}
              >
                {s.title}
              </div>
              <div
                className={cx(
                  styles.mStepDesc,
                  on(`text.${prefix}.step${s.n}.desc`, "color.textMuted"),
                )}
              >
                {s.desc}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Espejo: textos de pagina (titulo + descripcion) ----
function PageTextsMirror({ slot, hot }) {
  const on = mkOn(hot);
  const title = slot?.("text.pageTitle") || "Salas y Catalogo VR";
  const desc =
    slot?.("text.pageDescription") ||
    "Explora el catalogo de escenas VR, filtra por categorias y crea itinerarios.";
  return (
    <div className={styles.mPageTexts}>
      <div className={cx(styles.mPageTitle, on("text.pageTitle"))}>{title}</div>
      <div className={cx(styles.mPageDesc, on("text.pageDescription"))}>
        {desc}
      </div>
    </div>
  );
}

// ---- Espejo: tiles de categoria ----
// Icono = editable (slot de imagen). Punto = color programatico (fijo).
function TilesMirror({ slot, hot }) {
  const on = mkOn(hot);
  return (
    <div className={styles.mTilesBar}>
      {CATEGORY_TILES.map((tile, i) => {
        const icon = tile.slot ? slot?.(tile.slot) : null;
        return (
          <div
            key={tile.key}
            className={cx(
              styles.mTile,
              i === 0 && styles.mTileActive,
              on("color.surface", "color.border", "radius.card"),
              i === 0 && on("color.tileRing"),
            )}
          >
            <div
              className={cx(styles.mTileIcon, tile.slot && on(tile.slot))}
              style={{ background: `${tile.color}22` }}
            >
              {icon ? (
                <img src={icon} alt="" className={styles.mTileImg} />
              ) : (
                <AppstoreOutlined style={{ color: tile.color }} />
              )}
            </div>
            <span className={cx(styles.mTileLabel, on("color.text"))}>
              {tile.label}
            </span>
            <span
              className={styles.mTileDot}
              style={{ background: tile.color }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---- Espejo: chips de subcategoria ----
function ChipsMirror({ hot }) {
  const on = mkOn(hot);
  const chips = ["Prehistoria", "Edad Antigua", "Edad Media", "Edad Moderna"];
  return (
    <div className={styles.mChips}>
      {chips.map((c, i) => (
        <span
          key={c}
          className={cx(
            styles.mChip,
            i === 1 && styles.mChipActive,
            on("color.textMuted", "color.border"),
            i === 1 && on("color.chipActive"),
          )}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

// ---- Espejo: card de sala (escena) ----
function SceneCardMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div
      className={cx(
        styles.mScene,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div className={styles.mSceneThumb} />
      <div className={styles.mSceneBody}>
        <div className={cx(styles.mSceneCreated, on("color.textMuted"))}>
          Creado por Educa360
        </div>
        <div className={cx(styles.mSceneCat, on("color.textMuted"))}>
          <span className={styles.mSceneDot} /> HISTORIA
        </div>
        <div
          className={cx(styles.mSceneTitle, on("color.text", "font.display"))}
        >
          Villa de Bruto
        </div>
        <div className={styles.mSceneDivider} />
        <div className={styles.mSceneActions}>
          <span className={styles.mSceneIconBtn}>
            <EyeOutlined />
          </span>
          <span className={styles.mSceneIconBtn}>
            <UnorderedListOutlined />
          </span>
          <span className={cx(styles.mSceneMainBtn, on("color.cardAccent"))}>
            <LoginOutlined /> Anadir a itinerario
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- Espejo: card de itinerario ----
function ItineraryMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div
      className={cx(
        styles.mItin,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div className={styles.mItinHead}>
        <span className={cx(styles.mItinName, on("color.text"))}>
          Matematicas 1ESO
        </span>
        <span className={styles.mItinClass}>1A</span>
      </div>
      <span className={cx(styles.mItinTag, on("color.itinAccent"))}>
        Privada
      </span>
      <div className={styles.mItinActions}>
        <span className={cx(styles.mItinIcon, on("color.itinAccent"))}>
          <EyeOutlined />
        </span>
        <span className={cx(styles.mItinIcon, on("color.itinAccent"))}>
          <EditOutlined />
        </span>
        <span className={cx(styles.mItinIcon, styles.mItinIconDanger)}>
          <DeleteOutlined />
        </span>
      </div>
    </div>
  );
}

// ---- Espejo: modal crear/editar itinerario ----
function ModalMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div className={cx(styles.mModal, on("color.surface", "radius.card"))}>
      <div className={styles.mModalSteps}>
        <span
          className={cx(
            styles.mModalStep,
            styles.mModalStepActive,
            on("color.modalAccent"),
          )}
        >
          1
        </span>
        <span className={styles.mModalStepLine} />
        <span className={styles.mModalStep}>2</span>
        <span className={styles.mModalStepLine} />
        <span className={styles.mModalStep}>3</span>
      </div>
      <div className={cx(styles.mModalTitle, on("color.text"))}>
        Editar Itinerario
      </div>
      <div className={styles.mModalLabel}>Nombre del Itinerario</div>
      <div className={cx(styles.mModalInput, on("color.border"))}>
        Espana rutas geografia
      </div>
      <div className={styles.mModalActions}>
        <span className={styles.mModalGhost}>Cancelar</span>
        <span className={cx(styles.mModalPrimary, on("color.modalAccent"))}>
          Siguiente
        </span>
      </div>
    </div>
  );
}

// ---- Espejo: pestanas (Segmented) ----
function TabsMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div className={cx(styles.mTabs, on("color.surface", "radius.control"))}>
      <span
        className={cx(styles.mTab, styles.mTabActive, on("color.tabActive"))}
      >
        Asistentes Educa360
      </span>
      <span className={cx(styles.mTab, on("color.text"))}>Mis asistentes</span>
    </div>
  );
}

// ---- Espejo: card de asistente ----
function AssistantCardMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div
      className={cx(
        styles.mAsis,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div
        className={cx(
          styles.mAsisAvatar,
          on("radius.avatarShape", "radius.avatarSize"),
        )}
      />
      <div className={cx(styles.mAsisName, on("color.text", "font.display"))}>
        Marie Curie
      </div>
      <span className={cx(styles.mAsisTag, on("color.cardAccent"))}>
        <span className={styles.mAsisDot} /> Educa360
      </span>
      <span className={cx(styles.mAsisBtn, on("color.cardAccent"))}>
        <MessageOutlined /> Chatear
      </span>
    </div>
  );
}

// ---- Espejo: tabla de evaluaciones ----
function TableMirror({ hot }) {
  const on = mkOn(hot);
  const rows = [
    { t: "Historia", d: "Examen de Junio" },
    { t: "Quizz de Historia General", d: "Eventos historicos importantes" },
  ];
  return (
    <div
      className={cx(
        styles.mTable,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div className={cx(styles.mTableHead, on("color.text"))}>
        <span className={styles.mTableColT}>Titulo</span>
        <span className={styles.mTableColD}>Descripcion</span>
        <span className={styles.mTableColN}># Preg.</span>
        <span className={styles.mTableColA}>Acciones</span>
      </div>
      {rows.map((r) => (
        <div className={styles.mTableRow} key={r.t}>
          <span
            className={cx(styles.mTableColT, styles.mTableT, on("color.text"))}
          >
            {r.t}
          </span>
          <span
            className={cx(
              styles.mTableColD,
              styles.mTableD,
              on("color.textMuted"),
            )}
          >
            {r.d}
          </span>
          <span className={styles.mTableColN}>
            <span className={cx(styles.mTableBadge, on("color.tableAccent"))}>
              2
            </span>
          </span>
          <span className={styles.mTableColA}>
            <EditOutlined
              className={cx(styles.mTableIcon, on("color.tableAccent"))}
            />
            <EyeOutlined
              className={cx(styles.mTableIcon, on("color.tableAccent"))}
            />
            <DeleteOutlined
              className={cx(styles.mTableIcon, styles.mTableIconDanger)}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

// ---- Espejo: modal crear evaluacion (wizard) ----
function QuizModalMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div className={cx(styles.mQuiz, on("color.surface", "radius.card"))}>
      <div className={cx(styles.mQuizTitle, on("color.text"))}>
        Crear Evaluacion
      </div>
      <div className={styles.mQuizRow}>
        <div className={cx(styles.mQuizInput, on("color.border"))}>Nombre</div>
        <div className={cx(styles.mQuizInput, on("color.border"))}>
          Descripcion
        </div>
      </div>
      <div className={styles.mQuizBarTrack}>
        <div className={cx(styles.mQuizBarFill, on("color.modalAccent"))} />
      </div>
      <div className={cx(styles.mQuizTextarea, on("color.border"))}>
        Enunciado de la pregunta
      </div>
      <div className={styles.mQuizAnswers}>
        {[0, 1, 2].map((i) => (
          <div className={styles.mQuizAnswer} key={i}>
            <span
              className={cx(
                styles.mQuizRadio,
                i === 0 && styles.mQuizRadioOn,
                i === 0 && on("color.modalAccent"),
              )}
            />
            <span className={cx(styles.mQuizInput, on("color.border"))}>
              Respuesta {i + 1}
            </span>
          </div>
        ))}
      </div>
      <div className={styles.mQuizActions}>
        <span className={styles.mModalGhost}>Cancelar</span>
        <span className={cx(styles.mModalPrimary, on("color.modalAccent"))}>
          Crear evaluacion
        </span>
      </div>
    </div>
  );
}

// ---- Espejo: card de itinerario ----
function RouteCardMirror({ slot, hot }) {
  const on = mkOn(hot);
  const cover = slot?.("image.itinerario.cover");
  return (
    <div
      className={cx(
        styles.mRoute,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div className={cx(styles.mRouteCover, on("image.itinerario.cover"))}>
        {cover ? <img src={cover} alt="" /> : null}
        <span className={styles.mRouteBadge}>Del centro</span>
      </div>
      <div className={cx(styles.mRouteName, on("color.text"))}>
        Espana rutas geografia
      </div>
      <div className={cx(styles.mRouteAssigned, on("color.textMuted"))}>
        Asignado a 1A
      </div>
      <span className={cx(styles.mRouteBtn, on("color.cardAccent"))}>
        <LoginOutlined /> Entrar
      </span>
    </div>
  );
}

// ---- Espejo: panel de clase ----
function ClassPanelMirror({ hot }) {
  const on = mkOn(hot);
  const row = (key, name, desc) => (
    <div className={styles.mClassRow} key={key}>
      <span className={styles.mClassAvatar}>
        <RobotOutlined />
      </span>
      <div className={styles.mClassInfo}>
        <div className={cx(styles.mClassName, on("color.text"))}>{name}</div>
        <div className={cx(styles.mClassDesc, on("color.textMuted"))}>
          {desc}
        </div>
      </div>
      <span className={styles.mClassDot} />
    </div>
  );
  const section = (text) => (
    <div
      className={cx(
        styles.mClassSection,
        on("color.textMuted", "color.panelBar"),
      )}
    >
      {text}
    </div>
  );
  return (
    <div
      className={cx(
        styles.mClass,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div className={cx(styles.mClassHead, on("color.text"))}>Clase: 1A</div>
      {section("Asistentes IA asignados")}
      {row("a", "Marie Curie", "Asistente Virtual")}
      {section("Estudiantes en itinerario")}
      {row("s", "Alumno 1", "1A")}
    </div>
  );
}

// ---- Espejo: modal seleccionar test ----
function TestModalMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div className={cx(styles.mTest, on("color.surface", "radius.card"))}>
      <div className={cx(styles.mTestTitle, on("color.text"))}>
        Selecciona un Test para esta escena
      </div>
      {["Historia", "Quizz de Historia General"].map((opt, i) => (
        <div className={styles.mTestOption} key={opt}>
          <span
            className={cx(
              styles.mQuizRadio,
              i === 0 && styles.mQuizRadioOn,
              i === 0 && on("color.modalAccent"),
              on("color.border"),
            )}
          />
          <span className={cx(styles.mTestLabel, on("color.text"))}>{opt}</span>
        </div>
      ))}
      <div className={styles.mTestActions}>
        <span className={styles.mModalGhost}>Entrar sin Test</span>
        <span className={cx(styles.mModalPrimary, on("color.modalAccent"))}>
          Asignar y Entrar
        </span>
      </div>
    </div>
  );
}

// ---- Espejo: tabla de tareas ----
function TasksTableMirror({ hot }) {
  const on = mkOn(hot);
  const rows = [
    { t: "prueba", c: "1A", n: 2 },
    { t: "Repaso Tema 3", c: "1B", n: 4 },
  ];
  return (
    <div
      className={cx(
        styles.mTable,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div className={cx(styles.mTableHead, on("color.text"))}>
        <span className={styles.mTableColT}>Titulo</span>
        <span className={styles.mTasksColC}>Clase</span>
        <span className={styles.mTasksColN}># Pasos</span>
        <span className={styles.mTasksColE}>Estado</span>
        <span className={styles.mTableColA}>Acciones</span>
      </div>
      {rows.map((r) => (
        <div className={styles.mTableRow} key={r.t}>
          <span
            className={cx(styles.mTableColT, styles.mTableT, on("color.text"))}
          >
            {r.t}
          </span>
          <span className={cx(styles.mTasksColC, on("color.textMuted"))}>
            {r.c}
          </span>
          <span className={cx(styles.mTasksColN, on("color.textMuted"))}>
            {r.n}
          </span>
          <span className={styles.mTasksColE}>
            <span className={styles.mTasksStatus}>Activa</span>
          </span>
          <span className={styles.mTableColA}>
            <EditOutlined
              className={cx(styles.mTableIcon, on("color.tableAccent"))}
            />
            <PauseCircleOutlined
              className={cx(styles.mTableIcon, on("color.tableAccent"))}
            />
            <DeleteOutlined
              className={cx(styles.mTableIcon, styles.mTableIconDanger)}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

// ---- Espejo: asistente de creacion (wizard de 4 pasos) ----
function WizardMirror({ hot }) {
  const on = mkOn(hot);
  const steps = ["Clase", "Escenas", "Test", "Activar"];
  return (
    <div className={cx(styles.mWiz, on("color.surface", "radius.card"))}>
      {/* Pasos */}
      <div className={styles.mWizSteps}>
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <span className={styles.mWizStepBox}>
              <span
                className={cx(
                  styles.mWizStepDot,
                  i === 0 && styles.mWizStepOn,
                  i === 0 && on("color.wizardAccent"),
                )}
              >
                {i + 1}
              </span>
              <span
                className={cx(
                  styles.mWizStepLabel,
                  on(i === 0 ? "color.text" : "color.textMuted"),
                )}
              >
                {s}
              </span>
            </span>
            {i < steps.length - 1 ? (
              <span className={styles.mWizStepLine} />
            ) : null}
          </React.Fragment>
        ))}
      </div>

      {/* Tarjeta de seleccion */}
      <div
        className={cx(
          styles.mWizPick,
          on("color.border", "color.wizardAccent"),
        )}
      >
        <div className={styles.mWizPickThumb} />
        <div className={cx(styles.mWizPickName, on("color.text"))}>
          Espana rutas geografia
        </div>
        <span className={cx(styles.mWizPickBtn, on("color.wizardAccent"))}>
          <CheckOutlined /> Seleccionado
        </span>
      </div>

      {/* Flujo de la leccion */}
      <div className={styles.mWizFlow}>
        <span className={cx(styles.mWizOrder, on("color.wizardAccent"))}>
          1
        </span>
        <span className={cx(styles.mWizInstr, on("color.border"))}>
          Instruccion para el alumno...
        </span>
      </div>

      {/* Toggle cuestionario */}
      <div className={styles.mWizToggleRow}>
        <span className={cx(styles.mWizToggle, on("color.wizardAccent"))}>
          <span className={styles.mWizKnob} />
        </span>
        <span className={cx(styles.mWizToggleLabel, on("color.text"))}>
          Requerir cuestionario final
        </span>
      </div>

      {/* Footer */}
      <div className={styles.mWizActions}>
        <span className={styles.mModalGhost}>Cancelar</span>
        <span className={cx(styles.mWizPrimary, on("color.wizardAccent"))}>
          Siguiente
        </span>
      </div>
    </div>
  );
}

// ---- Espejo: card de escena creada (Educamaker) ----
function EduCardMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div
      className={cx(
        styles.mEdu,
        on("color.surface", "color.border", "radius.card"),
      )}
    >
      <div className={styles.mEduThumb} />
      <div className={styles.mEduBody}>
        <div className={cx(styles.mEduTitle, on("color.text", "font.display"))}>
          Taller Tecnologico
        </div>
        <div className={styles.mEduTag}>
          <span className={styles.mEduDot} /> TECNOLOGIA
        </div>
        <div className={cx(styles.mEduSub, on("color.textMuted"))}>Otros</div>
        <div className={styles.mEduActions}>
          <span className={cx(styles.mEduBtn, on("color.cardAccent"))}>
            <EyeOutlined /> Ver
          </span>
          <span className={cx(styles.mEduBtnGhost, on("color.cardAccent"))}>
            <EditOutlined /> Editar
          </span>
          <span className={cx(styles.mEduBtnDanger)}>
            <DeleteOutlined />
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- Espejo: dialogo de confirmacion (huerfano hoy; diseño = verde) ----
function ConfirmMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div className={cx(styles.mConfirm, on("color.surface", "radius.card"))}>
      <div className={cx(styles.mConfirmText, on("color.text"))}>
        ¿Estas seguro de que quieres cerrar este formulario?
      </div>
      <div className={cx(styles.mConfirmActions, on("color.border"))}>
        <span className={styles.mModalGhost}>Cancelar</span>
        <span className={cx(styles.mConfirmOk, on("color.confirmAccent"))}>
          OK
        </span>
      </div>
    </div>
  );
}

// ---- Espejo: marca (color primario global) ----
function BrandMirror({ hot }) {
  const on = mkOn(hot);
  return (
    <div className={styles.mBrand}>
      <div className={cx(styles.mBrandSwatch, on("color.primary"))} />
      <div className={styles.mBrandRow}>
        <span className={cx(styles.mBrandBtn, on("color.primary"))}>
          Boton primario
        </span>
        <span className={cx(styles.mBrandTag, on("color.primary"))}>
          Acento
        </span>
      </div>
      <div className={styles.mBrandNote}>
        Asi se vera en botones, acentos y elementos de toda la plataforma.
      </div>
    </div>
  );
}

// ---- Espejo: tipografia (titulos y texto) ----
function TypographyMirror() {
  return (
    <div className={styles.mTypo}>
      <div className={styles.mTypoTitle}>Titulo de ejemplo</div>
      <div className={styles.mTypoBody}>
        Texto de ejemplo para ver la fuente, el tamano, el peso y la cursiva del
        cuerpo de la plataforma.
      </div>
      <span className={styles.mTypoBtn}>Boton de ejemplo</span>
    </div>
  );
}

const PREVIEWS = {
  sidebar: SidebarMirror,
  topbar: TopbarMirror,
  guide: GuideMirror,
  pageTexts: PageTextsMirror,
  tiles: TilesMirror,
  chips: ChipsMirror,
  sceneCard: SceneCardMirror,
  itinerary: ItineraryMirror,
  modal: ModalMirror,
  typography: TypographyMirror,
  tabs: TabsMirror,
  assistantCard: AssistantCardMirror,
  table: TableMirror,
  quizModal: QuizModalMirror,
  routeCard: RouteCardMirror,
  classPanel: ClassPanelMirror,
  testModal: TestModalMirror,
  tasksTable: TasksTableMirror,
  wizard: WizardMirror,
  eduCard: EduCardMirror,
  confirm: ConfirmMirror,
  brand: BrandMirror,
};

/**
 * Espejo de un bloque. `hot` (bloque abierto) ilumina el contorno del bloque.
 * `hotCtrl` = { block, id }: solo se ilumina el elemento si el control con hover
 * pertenece a ESTE bloque (resaltado confinado a su propio espejo).
 */
export function BlockPreview({ block, hot, hotCtrl, slot }) {
  const Cmp = PREVIEWS[block.preview];
  if (!Cmp) return null;
  const hotId = hotCtrl && hotCtrl.block === block.key ? hotCtrl.id : null;
  return (
    <div className={cx(styles.pvBlock, hot && styles.pvBlockHot)}>
      <div className={styles.pvBlockLabel}>{block.label}</div>
      <Cmp slot={slot} block={block} hot={hotId} />
    </div>
  );
}

export default BlockPreview;
